import {
  type CleanupReport,
  type CleanupStageResult,
  PROTECTED_DATABASE_NAMES,
  isProtectedRoute,
  isProtectedStorageKey,
  isShellCacheName,
  isWhitehavenCacheName,
  shouldDeleteCacheAtLogout,
} from "./protected-state-model.ts";
import {
  requestProtectedCacheClear,
  type ProtectedCacheClearRequestOptions,
} from "./service-worker-protocol.ts";

export const LOGOUT_CHANNEL_NAME = "whitehaven-logout-v1";
export const LOGOUT_MESSAGE_TYPE = "WHITEHAVEN_LOGOUT";
export const LOGOUT_MESSAGE_VERSION = 1;
export const DEFAULT_CLIENT_CLEANUP_TIMEOUT_MS = 4_000;
export const DEFAULT_REMOTE_LOGOUT_TIMEOUT_MS = 10_000;
export const DEFAULT_TOTAL_CLIENT_CLEANUP_TIMEOUT_MS = 13_000;

export interface ProtectedStateCleanerContext {
  readonly generation: number;
}

export type ProtectedStateCleaner = (
  context: ProtectedStateCleanerContext,
) => void | Promise<void>;

export interface ProtectedStateCleanupOptions {
  readonly localStorage?: Storage | null;
  readonly sessionStorage?: Storage | null;
  readonly indexedDB?: IDBFactory | null;
  readonly cacheStorage?: CacheStorage | null;
  readonly origin?: string | null;
  readonly serviceWorker?: ProtectedCacheClearRequestOptions | null;
  readonly stageTimeoutMs?: number;
}

interface ResolvedApi<T> {
  readonly value: T | null;
  readonly accessFailed: boolean;
}

interface BroadcastChannelLike {
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  postMessage(message: unknown): void;
  close(): void;
}

export interface LogoutSyncOptions {
  readonly clear?: () => Promise<CleanupReport>;
  readonly createBroadcastChannel?: (
    channelName: string,
  ) => BroadcastChannelLike;
  readonly createEventId?: () => string;
}

export interface LogoutSyncController {
  clearEverywhere(): Promise<CleanupReport>;
  stop(): void;
}

export type PortalLogoutFailureCode =
  | "SUBJECT_CLEAR_FAILED"
  | "SESSION_INVALIDATION_FAILED"
  | "IDENTITY_SIGN_OUT_FAILED"
  | "CLIENT_CLEANUP_FAILED"
  | "SIGNED_OUT_NAVIGATION_FAILED";

export interface FailClosedLogoutDependencies {
  readonly clearTrustedSubject: () => void;
  readonly invalidateApplicationSession: () => Promise<void>;
  readonly signOutIdentityProvider: () => Promise<void>;
  readonly clearClientState?: () => Promise<CleanupReport>;
  readonly navigateToSignedOut: () => void;
  readonly remoteTimeoutMs?: number;
  readonly clientCleanupTimeoutMs?: number;
}

export interface FailClosedLogoutResult {
  readonly status: "complete" | "unconfirmed";
  readonly cleanup: CleanupReport;
  readonly failures: readonly PortalLogoutFailureCode[];
}

const CLEANER_ID_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;
const EVENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const registeredCleaners = new Map<string, ProtectedStateCleaner>();

let protectedStateGeneration = 0;
let cleanupInFlight: Promise<CleanupReport> | null = null;
let logoutEventSequence = 0;
let sharedLogoutSync: LogoutSyncController | null = null;

function report(
  generation: number,
  stages: readonly CleanupStageResult[],
): CleanupReport {
  return {
    status: stages.some((stage) => stage.status === "failed")
      ? "partial"
      : "cleared",
    generation,
    stages,
  };
}

function skipped(id: string, code: string): CleanupStageResult {
  return { id, status: "skipped", code };
}

function failed(id: string, code: string): CleanupStageResult {
  return { id, status: "failed", code };
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    (typeof value === "object" || typeof value === "function") &&
    value !== null &&
    "then" in value &&
    typeof value.then === "function"
  );
}

function cleared(id: string): CleanupStageResult {
  return { id, status: "cleared" };
}

function normalizeTimeout(timeoutMs: number | undefined): number {
  if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs)) {
    return DEFAULT_CLIENT_CLEANUP_TIMEOUT_MS;
  }

  return Math.max(1, Math.min(timeoutMs, DEFAULT_CLIENT_CLEANUP_TIMEOUT_MS));
}

function normalizeRemoteTimeout(timeoutMs: number | undefined): number {
  if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs)) {
    return DEFAULT_REMOTE_LOGOUT_TIMEOUT_MS;
  }

  return Math.max(1, Math.min(timeoutMs, DEFAULT_REMOTE_LOGOUT_TIMEOUT_MS));
}

function normalizeTotalCleanupTimeout(timeoutMs: number | undefined): number {
  if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs)) {
    return DEFAULT_TOTAL_CLIENT_CLEANUP_TIMEOUT_MS;
  }

  return Math.max(
    1,
    Math.min(timeoutMs, DEFAULT_TOTAL_CLIENT_CLEANUP_TIMEOUT_MS),
  );
}

function runBoundedRemoteOperation(
  operation: () => Promise<void>,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = (succeeded: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      resolve(succeeded);
    };

    timeoutId = setTimeout(() => finish(false), timeoutMs);
    void Promise.resolve()
      .then(operation)
      .then(
        () => finish(true),
        () => finish(false),
      );
  });
}

function withStageTimeout(
  operation: Promise<CleanupStageResult>,
  id: string,
  code: string,
  timeoutMs: number,
): Promise<CleanupStageResult> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = (result: CleanupStageResult) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      resolve(result);
    };
    timeoutId = setTimeout(() => finish(failed(id, code)), timeoutMs);

    void operation.then(
      (result) => finish(result),
      () => finish(failed(id, code)),
    );
  });
}

function runBoundedClientCleanup(
  operation: Promise<CleanupReport>,
  timeoutMs: number,
): Promise<CleanupReport> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = (cleanup: CleanupReport) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      resolve(cleanup);
    };

    timeoutId = setTimeout(
      () => finish(failedCleanupReport("CLIENT_CLEANUP_TIMEOUT")),
      timeoutMs,
    );
    void operation.then(
      (cleanup) => finish(cleanup),
      () => finish(failedCleanupReport()),
    );
  });
}

function resolveWindowStorage(
  kind: "localStorage" | "sessionStorage",
): ResolvedApi<Storage> {
  if (typeof window === "undefined") {
    return { value: null, accessFailed: false };
  }

  try {
    return { value: window[kind], accessFailed: false };
  } catch {
    return { value: null, accessFailed: true };
  }
}

function resolveGlobalApi<T>(read: () => T | undefined): ResolvedApi<T> {
  try {
    return { value: read() ?? null, accessFailed: false };
  } catch {
    return { value: null, accessFailed: true };
  }
}

function resolveStorage(
  options: ProtectedStateCleanupOptions,
  kind: "localStorage" | "sessionStorage",
): ResolvedApi<Storage> {
  if (kind in options) {
    return {
      value: options[kind] ?? null,
      accessFailed: false,
    };
  }

  return resolveWindowStorage(kind);
}

function resolveOrigin(options: ProtectedStateCleanupOptions): string | null {
  if ("origin" in options) {
    return options.origin ?? null;
  }

  try {
    return typeof location === "undefined" ? null : location.origin;
  } catch {
    return null;
  }
}

async function clearStorage(
  id: string,
  resolved: ResolvedApi<Storage>,
): Promise<CleanupStageResult> {
  if (resolved.accessFailed) {
    return failed(id, "STORAGE_ACCESS_FAILED");
  }

  if (!resolved.value) {
    return skipped(id, "STORAGE_UNAVAILABLE");
  }

  try {
    const keys: string[] = [];

    for (let index = 0; index < resolved.value.length; index += 1) {
      const key = resolved.value.key(index);
      if (key !== null && isProtectedStorageKey(key)) {
        keys.push(key);
      }
    }

    let removalFailed = false;
    for (const key of keys) {
      try {
        resolved.value.removeItem(key);
      } catch {
        removalFailed = true;
      }
    }

    return removalFailed ? failed(id, "STORAGE_CLEANUP_FAILED") : cleared(id);
  } catch {
    return failed(id, "STORAGE_CLEANUP_FAILED");
  }
}

function deleteProtectedDatabase(
  databaseFactory: IDBFactory,
  databaseName: string,
  timeoutMs: number,
): Promise<CleanupStageResult> {
  const id = `indexed-db:${databaseName}`;

  return new Promise((resolve) => {
    let settled = false;
    let wasBlocked = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = (result: CleanupStageResult) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      resolve(result);
    };
    timeoutId = setTimeout(
      () =>
        finish(
          failed(
            id,
            wasBlocked
              ? "INDEXED_DB_DELETE_BLOCKED"
              : "INDEXED_DB_DELETE_TIMEOUT",
          ),
        ),
      timeoutMs,
    );

    try {
      const request = databaseFactory.deleteDatabase(databaseName);
      request.onsuccess = () => finish(cleared(id));
      request.onerror = () => finish(failed(id, "INDEXED_DB_DELETE_FAILED"));
      request.onblocked = () => {
        wasBlocked = true;
      };
    } catch {
      finish(failed(id, "INDEXED_DB_DELETE_FAILED"));
    }
  });
}

async function clearIndexedDatabases(
  resolved: ResolvedApi<IDBFactory>,
  timeoutMs: number,
): Promise<readonly CleanupStageResult[]> {
  if (resolved.accessFailed) {
    return [failed("indexed-db", "INDEXED_DB_ACCESS_FAILED")];
  }

  if (!resolved.value) {
    return [skipped("indexed-db", "INDEXED_DB_UNAVAILABLE")];
  }

  return Promise.all(
    PROTECTED_DATABASE_NAMES.map((databaseName) =>
      deleteProtectedDatabase(resolved.value!, databaseName, timeoutMs),
    ),
  );
}

function requestPathIsProtected(
  request: Request,
  expectedOrigin: string | null,
): boolean {
  if (!expectedOrigin) {
    return false;
  }

  try {
    const requestUrl = new URL(request.url);
    return (
      requestUrl.origin === expectedOrigin &&
      isProtectedRoute(requestUrl.pathname)
    );
  } catch {
    return false;
  }
}

async function clearCacheStorage(
  resolved: ResolvedApi<CacheStorage>,
  origin: string | null,
): Promise<CleanupStageResult> {
  if (resolved.accessFailed) {
    return failed("cache-storage", "CACHE_STORAGE_ACCESS_FAILED");
  }

  if (!resolved.value) {
    return skipped("cache-storage", "CACHE_STORAGE_UNAVAILABLE");
  }

  try {
    const cacheNames = await resolved.value.keys();
    let shellScanUnavailable = false;
    const actions = cacheNames
      .filter((cacheName) => isWhitehavenCacheName(cacheName))
      .map(async (cacheName) => {
        if (shouldDeleteCacheAtLogout(cacheName)) {
          await resolved.value!.delete(cacheName);
          return;
        }

        if (!isShellCacheName(cacheName)) {
          return;
        }

        if (!origin) {
          shellScanUnavailable = true;
          return;
        }

        const cache = await resolved.value!.open(cacheName);
        const requests = await cache.keys();
        await Promise.all(
          requests
            .filter((request) => requestPathIsProtected(request, origin))
            .map((request) => cache.delete(request)),
        );
      });

    const outcomes = await Promise.allSettled(actions);
    if (outcomes.some((outcome) => outcome.status === "rejected")) {
      return failed("cache-storage", "CACHE_CLEANUP_FAILED");
    }

    return shellScanUnavailable
      ? failed("cache-storage", "CACHE_ORIGIN_UNAVAILABLE")
      : cleared("cache-storage");
  } catch {
    return failed("cache-storage", "CACHE_CLEANUP_FAILED");
  }
}

function runCleaner(
  cleanerId: string,
  cleaner: ProtectedStateCleaner,
  generation: number,
  timeoutMs: number,
): Promise<CleanupStageResult> {
  const id = `memory:${cleanerId}`;

  try {
    const operation = Promise.resolve(cleaner({ generation })).then(
      () => cleared(id),
      () => failed(id, "MEMORY_CLEANER_FAILED"),
    );
    return withStageTimeout(operation, id, "MEMORY_CLEANER_TIMEOUT", timeoutMs);
  } catch {
    return Promise.resolve(failed(id, "MEMORY_CLEANER_FAILED"));
  }
}

async function runProtectedStateCleanup(
  options: ProtectedStateCleanupOptions,
): Promise<CleanupReport> {
  protectedStateGeneration += 1;
  const generation = protectedStateGeneration;
  const cleaners = [...registeredCleaners.entries()];
  const stageTimeoutMs = normalizeTimeout(options.stageTimeoutMs);

  const localStorage = resolveStorage(options, "localStorage");
  const sessionStorage = resolveStorage(options, "sessionStorage");
  const databaseFactory =
    "indexedDB" in options
      ? { value: options.indexedDB ?? null, accessFailed: false }
      : resolveGlobalApi(() =>
          typeof indexedDB === "undefined" ? undefined : indexedDB,
        );
  const cacheStorage =
    "cacheStorage" in options
      ? { value: options.cacheStorage ?? null, accessFailed: false }
      : resolveGlobalApi(() =>
          typeof caches === "undefined" ? undefined : caches,
        );

  const memoryPromise =
    cleaners.length === 0
      ? Promise.resolve<readonly CleanupStageResult[]>([
          skipped("memory", "NO_MEMORY_CLEANERS"),
        ])
      : Promise.all(
          cleaners.map(([cleanerId, cleaner]) =>
            runCleaner(cleanerId, cleaner, generation, stageTimeoutMs),
          ),
        );

  const memoryStages = await memoryPromise;
  const [localStage, sessionStage, databaseStages, cacheStage] =
    await Promise.all([
      clearStorage("local-storage", localStorage),
      clearStorage("session-storage", sessionStorage),
      clearIndexedDatabases(databaseFactory, stageTimeoutMs),
      withStageTimeout(
        clearCacheStorage(cacheStorage, resolveOrigin(options)),
        "cache-storage",
        "CACHE_CLEANUP_TIMEOUT",
        stageTimeoutMs,
      ),
    ]);

  const serviceWorkerOptions =
    "serviceWorker" in options
      ? (options.serviceWorker ?? { controller: null })
      : {};
  const serviceWorkerResult =
    await requestProtectedCacheClear(serviceWorkerOptions);
  const serviceWorkerStage: CleanupStageResult =
    serviceWorkerResult.status === "cleared"
      ? cleared("service-worker")
      : {
          id: "service-worker",
          status: serviceWorkerResult.status,
          code: serviceWorkerResult.code,
        };

  return report(generation, [
    ...memoryStages,
    localStage,
    sessionStage,
    ...databaseStages,
    cacheStage,
    serviceWorkerStage,
  ]);
}

function defaultBroadcastChannelFactory(
  channelName: string,
): BroadcastChannelLike {
  return new BroadcastChannel(channelName);
}

function createDefaultLogoutEventId(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  logoutEventSequence += 1;
  return `logout-${logoutEventSequence}`;
}

function isLogoutMessage(value: unknown): boolean {
  if (!value || typeof value !== "object") {
    return false;
  }

  const message = value as Record<string, unknown>;
  const keys = Object.keys(message);
  return (
    keys.length === 3 &&
    keys.every((key) => ["type", "version", "eventId"].includes(key)) &&
    message.type === LOGOUT_MESSAGE_TYPE &&
    message.version === LOGOUT_MESSAGE_VERSION &&
    typeof message.eventId === "string" &&
    EVENT_ID_PATTERN.test(message.eventId)
  );
}

function addStage(
  cleanup: CleanupReport,
  stage: CleanupStageResult,
): CleanupReport {
  return report(cleanup.generation, [...cleanup.stages, stage]);
}

function failedCleanupReport(code = "CLIENT_CLEANUP_FAILED"): CleanupReport {
  return report(protectedStateGeneration, [failed("client-cleanup", code)]);
}

export function registerProtectedStateCleaner(
  cleanerId: string,
  cleaner: ProtectedStateCleaner,
): () => void {
  if (!CLEANER_ID_PATTERN.test(cleanerId)) {
    throw new Error("Protected state cleaner IDs must be static safe names.");
  }

  if (registeredCleaners.has(cleanerId)) {
    throw new Error(`Protected state cleaner "${cleanerId}" is registered.`);
  }

  registeredCleaners.set(cleanerId, cleaner);

  return () => {
    if (registeredCleaners.get(cleanerId) === cleaner) {
      registeredCleaners.delete(cleanerId);
    }
  };
}

export function captureProtectedStateGeneration(): number {
  return protectedStateGeneration;
}

export function isProtectedStateGenerationCurrent(generation: number): boolean {
  return generation === protectedStateGeneration;
}

export function clearProtectedClientState(
  options: ProtectedStateCleanupOptions = {},
): Promise<CleanupReport> {
  if (cleanupInFlight) {
    return cleanupInFlight;
  }

  const cleanup = runProtectedStateCleanup(options).catch(() =>
    failedCleanupReport(),
  );
  cleanupInFlight = cleanup;

  void cleanup.finally(() => {
    if (cleanupInFlight === cleanup) {
      cleanupInFlight = null;
    }
  });

  return cleanup;
}

export function startLogoutSync(
  options: LogoutSyncOptions = {},
): LogoutSyncController {
  const clear = options.clear ?? (() => clearProtectedClientState());
  let channel: BroadcastChannelLike | null = null;
  let channelCreationFailed = false;
  let stopped = false;

  try {
    const createChannel =
      options.createBroadcastChannel ?? defaultBroadcastChannelFactory;
    channel =
      typeof BroadcastChannel === "undefined" && !options.createBroadcastChannel
        ? null
        : createChannel(LOGOUT_CHANNEL_NAME);
  } catch {
    channelCreationFailed = true;
  }

  if (channel) {
    try {
      channel.onmessage = (event) => {
        if (!stopped && isLogoutMessage(event.data)) {
          void Promise.resolve()
            .then(clear)
            .catch(() => undefined);
        }
      };
    } catch {
      channelCreationFailed = true;
      try {
        channel.close();
      } catch {
        // A broken channel is represented in the structured result.
      }
      channel = null;
    }
  }

  return {
    async clearEverywhere() {
      let broadcastStage: CleanupStageResult;

      if (!channel) {
        broadcastStage = channelCreationFailed
          ? failed("broadcast", "BROADCAST_CHANNEL_FAILED")
          : skipped("broadcast", "BROADCAST_CHANNEL_UNAVAILABLE");
      } else {
        try {
          const eventId = (
            options.createEventId ?? createDefaultLogoutEventId
          )();
          if (!EVENT_ID_PATTERN.test(eventId)) {
            throw new Error("Invalid logout event ID.");
          }

          channel.postMessage({
            type: LOGOUT_MESSAGE_TYPE,
            version: LOGOUT_MESSAGE_VERSION,
            eventId,
          });
          broadcastStage = cleared("broadcast");
        } catch {
          broadcastStage = failed("broadcast", "BROADCAST_POST_FAILED");
        }
      }

      try {
        return addStage(await clear(), broadcastStage);
      } catch {
        return addStage(failedCleanupReport(), broadcastStage);
      }
    },
    stop() {
      if (stopped) {
        return;
      }

      stopped = true;
      if (channel) {
        try {
          channel.onmessage = null;
          channel.close();
        } catch {
          // Disposal is best-effort and must remain idempotent.
        }
        channel = null;
      }
    },
  };
}

export function startPortalLogoutSync(): LogoutSyncController {
  sharedLogoutSync ??= startLogoutSync();
  return sharedLogoutSync;
}

export function stopPortalLogoutSync(): void {
  sharedLogoutSync?.stop();
  sharedLogoutSync = null;
}

export function clearProtectedClientStateEverywhere(): Promise<CleanupReport> {
  return startPortalLogoutSync().clearEverywhere();
}

export async function performFailClosedLogout(
  dependencies: FailClosedLogoutDependencies,
): Promise<FailClosedLogoutResult> {
  const failures: PortalLogoutFailureCode[] = [];
  let subjectClearFailed = false;
  let navigationSucceeded = false;
  const addFailure = (code: PortalLogoutFailureCode) => {
    if (!failures.includes(code)) {
      failures.push(code);
    }
  };
  const navigateToSignedOut = () => {
    if (navigationSucceeded) {
      return;
    }

    try {
      const navigationResult = dependencies.navigateToSignedOut();
      if (isPromiseLike(navigationResult)) {
        void Promise.resolve(navigationResult).catch(() => undefined);
        addFailure("SIGNED_OUT_NAVIGATION_FAILED");
        return;
      }
      navigationSucceeded = true;
    } catch {
      addFailure("SIGNED_OUT_NAVIGATION_FAILED");
    }
  };

  try {
    const subjectClearResult = dependencies.clearTrustedSubject();
    if (isPromiseLike(subjectClearResult)) {
      void Promise.resolve(subjectClearResult).catch(() => undefined);
      addFailure("SUBJECT_CLEAR_FAILED");
      subjectClearFailed = true;
    }
  } catch {
    addFailure("SUBJECT_CLEAR_FAILED");
    subjectClearFailed = true;
  }

  let cleanupPromise: Promise<CleanupReport>;
  try {
    const requestedCleanup = (
      dependencies.clearClientState ?? clearProtectedClientStateEverywhere
    )().catch(() => {
      addFailure("CLIENT_CLEANUP_FAILED");
      return failedCleanupReport();
    });
    cleanupPromise = runBoundedClientCleanup(
      requestedCleanup,
      normalizeTotalCleanupTimeout(dependencies.clientCleanupTimeoutMs),
    );
  } catch {
    addFailure("CLIENT_CLEANUP_FAILED");
    cleanupPromise = Promise.resolve(failedCleanupReport());
  }

  if (subjectClearFailed) {
    navigateToSignedOut();
  }

  const remoteTimeoutMs = normalizeRemoteTimeout(dependencies.remoteTimeoutMs);
  const remoteResults = await Promise.all([
    runBoundedRemoteOperation(
      dependencies.invalidateApplicationSession,
      remoteTimeoutMs,
    ),
    runBoundedRemoteOperation(
      dependencies.signOutIdentityProvider,
      remoteTimeoutMs,
    ),
  ]);

  if (!remoteResults[0]) {
    addFailure("SESSION_INVALIDATION_FAILED");
  }
  if (!remoteResults[1]) {
    addFailure("IDENTITY_SIGN_OUT_FAILED");
  }

  const cleanup = await cleanupPromise;

  if (cleanup.status === "partial") {
    addFailure("CLIENT_CLEANUP_FAILED");
  }

  navigateToSignedOut();

  return {
    status: failures.length === 0 ? "complete" : "unconfirmed",
    cleanup,
    failures,
  };
}
